/**
 * The deep link a signed-out visitor asked for, validated — NFR-3.
 *
 * A door that sends you wherever it is told is an open redirect: put an
 * attacker's URL in front of the OAuth round trip and the app itself delivers
 * the visitor there, from its own origin, immediately after a successful
 * Patreon sign-in — the moment they are least suspicious. That is why this
 * function exists rather than the path being passed through.
 *
 * Used here where the path is handed to `signInUrl`. **The other end moved to
 * the server** at task 9: `returnTo` no longer comes back to this page, it is
 * signed into the state cookie and the API redirects to it directly. The
 * backend keeps its own copy of this function — deliberately, see
 * `ReturnTo.ts` — and validates on both write and consumption there.
 *
 * Both ends still matter, they are just no longer both here: the value we send
 * is ours, and the value that returns has been through the OAuth `state`
 * parameter, so a signature proving we wrote it does not prove it was safe.
 *
 * Returns the path, or `null`. Never throws, and never repairs a bad value: a
 * caller that gets `null` falls back to the identity's own default route
 * (FR-3), which is the answer that cannot be manipulated.
 */

/**
 * A base used only to resolve the candidate for comparison. Any absolute URL
 * would do — what matters is that a value which escapes to *any* other origin
 * lands somewhere other than here.
 */
const SENTINEL_ORIGIN = "https://return-to.invalid";

export function safeReturnTo(value: unknown): string | null {
  // The value arrives from navigation state typed as `any`, so this is a runtime
  // check rather than a formality.
  if (typeof value !== "string") return null;

  /*
   * Must be a path, not a URL, and not a bare relative segment.
   *
   * This is doing real work that the origin check below cannot: `new URL()`
   * resolves a relative segment like "tabs/x" against the base *path*, so it
   * stays on the sentinel origin and passes. It is still not a route in this
   * app, and accepting it means the FR-3 fallback never runs where it should.
   * "" is refused here too, which is right - the absence of a destination is
   * not a request for the root.
   */
  if (!value.startsWith("/")) return null;

  /*
   * The check that decides. Resolve the candidate and confirm it did not leave.
   *
   * This one line subsumes three explicit guards that were written first and
   * then removed, because none of them could be made to fail on its own:
   *
   * - `//evil.test`, protocol-relative — keeps our scheme, changes the host.
   * - `/\evil.test`, where a URL parser reads the backslash as a slash, so it
   *   resolves exactly like the above while looking like a path.
   * - `/<tab>/evil.test`, where the browser strips the tab *before* parsing,
   *   so it is fetched as `//evil.test` and sails past a naive `startsWith`.
   *
   * All three are the same question - "does this still point at us?" - and
   * this is the form of it that also catches the fourth one nobody has thought
   * of yet. Per W-6, three guards that cannot individually be observed to fail
   * are not three layers of defence; they are one check and two claims nobody
   * can check. The tests for all three attacks stay, and now exercise this.
   *
   * Both jsdom and every browser implement the WHATWG URL parser, so the
   * backslash and control-character normalisation the tests rely on is
   * specified behaviour rather than an implementation detail.
   */
  try {
    if (new URL(value, SENTINEL_ORIGIN).origin !== SENTINEL_ORIGIN) return null;
  } catch {
    return null;
  }

  return value;
}
